/**
 * Хуки данных очереди F&B (кухня / бар / официант), Спринт 11 блок 4.
 *
 * Бэк (internal/request): GET /v1/admin/requests?state=&category= отдаёт
 * заявки отеля (менеджер/владелец/персонал через role-exception).
 * Очередь — это заявки-заказы из меню (категории каталога с кодом
 * `kitchen_*` / `bar_*`, засиженные в tenant_oblaka). Раздельность кухни и
 * бара — фильтр по префиксу кода категории заявки (без серверного фильтра по
 * отделу): кухня видит только `kitchen_*`, бар — `bar_*` (вкл. кальян),
 * официант/менеджер — оба.
 *
 * Переходы: assign (Принять) → start (Готовится) → resolve (Готово).
 * Сервер-стейт только через React Query; после мутации — инвалидация ленты.
 */
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  assignRequest,
  deliverRequest,
  listManagerRequests,
  resolveRequest,
  startRequest,
} from '@hsc/api-client';
import type { ManagerRequest } from '@hsc/types';

/** Каким цехам принадлежит заявка (по префиксу кода категории каталога). */
export type FnbDept = 'kitchen' | 'bar';

/**
 * Маркеры «барных» позиций для категории room_service (общая категория
 * «в номер»: содержит и еду, и напитки/кальян). Заказы room_service по
 * умолчанию идут на кухню, но если в названии/описании есть барный маркер
 * (кальян, коктейль, напитки, вино и т.п.) — это бар. Без этого «Кальян на
 * террасу» уезжал на кухню, а очередь бара оставалась пустой (бриф «Облака»
 * #30: кухня и бар — раздельно).
 */
const BAR_HINTS = [
  'кальян',
  'бар',
  'коктейл',
  'напит',
  'вино',
  'винн',
  'пиво',
  'виски',
  'мартини',
  'шампанск',
  'лимонад',
  'смузи',
  'глинтвейн',
  'аперол',
  'просекко',
];

function hasBarHint(req: ManagerRequest): boolean {
  const text = `${req.title ?? ''} ${req.description ?? ''}`.toLowerCase();
  return BAR_HINTS.some((h) => text.includes(h));
}

/** Определяет цех заявки по коду её категории. null — не F&B-заказ. */
export function deptOfRequest(req: ManagerRequest): FnbDept | null {
  const c = (req.category ?? '').toLowerCase();
  if (c.startsWith('bar_') || c === 'minibar') return 'bar';
  if (c.startsWith('kitchen_')) return 'kitchen';
  // room_service — общая категория «в номер»: делим по содержимому заказа,
  // чтобы напитки/кальян попадали бармену, а еда — на кухню.
  if (c === 'room_service') return hasBarHint(req) ? 'bar' : 'kitchen';
  return null;
}

/**
 * Лента F&B-заказов отеля. Тянем разом (без серверного category-фильтра,
 * т.к. категорий много), затем делим по цеху на клиенте.
 */
export function useFnbQueue() {
  const query = useQuery({
    queryKey: ['fnb-queue', 'admin-requests'],
    queryFn: () => listManagerRequests({ limit: 200 }),
    staleTime: 10_000,
    refetchInterval: 20_000,
  });

  const fnb = useMemo<ManagerRequest[]>(
    () => (query.data?.items ?? []).filter((r) => deptOfRequest(r) !== null),
    [query.data],
  );

  return { ...query, fnb };
}

/** Мутации перехода статуса заказа с инвалидацией очереди. */
export function useFnbTransitions() {
  const qc = useQueryClient();
  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['fnb-queue'] });
    void qc.invalidateQueries({ queryKey: ['requests'] });
  };

  const accept = useMutation({
    mutationFn: ({ id, staffId }: { id: string; staffId: string }) =>
      assignRequest(id, staffId),
    onSuccess: invalidate,
  });
  const start = useMutation({
    mutationFn: (id: string) => startRequest(id),
    onSuccess: invalidate,
  });
  const finish = useMutation({
    mutationFn: (id: string) => resolveRequest(id),
    onSuccess: invalidate,
  });
  // Доставлено (resolved → delivered), 4-я колонка §9.5.
  const deliver = useMutation({
    mutationFn: (id: string) => deliverRequest(id),
    onSuccess: invalidate,
  });

  return { accept, start, finish, deliver };
}
